import { ChartLine, Question, Clock, TrendUp, TrendDown, Target, ShieldCheck, Warning, CaretUp, CaretDown, Equals, Scales, Sparkle, ArrowRight, Gauge } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";

function Section({ icon: Icon, title, subtitle, tone, children, testid }) {
  const tones = {
    amber: "border-amber-500/30 bg-amber-500/5",
    emerald: "border-emerald-500/25 bg-emerald-500/5",
    blue: "border-blue-500/25 bg-blue-500/5",
    rose: "border-rose-500/25 bg-rose-500/5",
  };
  const iconTones = {
    amber: "text-amber-400 bg-amber-500/10 border-amber-500/30",
    emerald: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
    blue: "text-blue-400 bg-blue-500/10 border-blue-500/30",
    rose: "text-rose-400 bg-rose-500/10 border-rose-500/30",
  };
  return (
    <section className={`rounded-sm border ${tones[tone]} p-5 sm:p-6`} data-testid={testid}>
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-10 h-10 rounded-sm border flex items-center justify-center shrink-0 ${iconTones[tone]}`}>
          <Icon size={20} weight="bold" />
        </div>
        <div className="min-w-0">
          <h2 className="font-heading text-lg sm:text-xl font-bold text-zinc-50">{title}</h2>
          {subtitle && <p className="text-[11px] sm:text-xs text-zinc-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="space-y-3 text-sm text-zinc-300 leading-relaxed">{children}</div>
    </section>
  );
}

// Visual SVG candlestick — draws a single candle with wick
function Candle({ color = "#10b981", bodyTop, bodyBottom, wickTop, wickBottom, label, x = 40 }) {
  return (
    <g>
      <line x1={x} y1={wickTop} x2={x} y2={wickBottom} stroke={color} strokeWidth="1.5" />
      <rect x={x - 9} y={bodyTop} width="18" height={bodyBottom - bodyTop} fill={color} rx="1" />
      {label && (
        <text x={x} y={wickBottom + 14} textAnchor="middle" fontSize="9" fill="#a1a1aa" fontFamily="ui-monospace, monospace">{label}</text>
      )}
    </g>
  );
}

function BullBearDiagram() {
  // Two side-by-side candles with labeled parts
  return (
    <svg viewBox="0 0 320 180" className="w-full max-w-md mx-auto" aria-label="Bull and bear candle diagram">
      {/* Price axis hint */}
      <line x1="0" y1="20" x2="320" y2="20" stroke="#27272a" strokeDasharray="2 3" />
      <line x1="0" y1="160" x2="320" y2="160" stroke="#27272a" strokeDasharray="2 3" />

      {/* Bullish (green) */}
      <Candle color="#10b981" bodyTop={55} bodyBottom={130} wickTop={25} wickBottom={160} label="BULL" x={80} />
      {/* Annotations */}
      <line x1="112" y1="55" x2="140" y2="55" stroke="#71717a" strokeWidth="0.5" />
      <text x="144" y="58" fontSize="9" fill="#e4e4e7">Close</text>
      <line x1="112" y1="130" x2="140" y2="130" stroke="#71717a" strokeWidth="0.5" />
      <text x="144" y="133" fontSize="9" fill="#e4e4e7">Open</text>
      <line x1="80" y1="25" x2="50" y2="25" stroke="#71717a" strokeWidth="0.5" />
      <text x="18" y="28" fontSize="9" fill="#e4e4e7">High</text>
      <line x1="80" y1="160" x2="50" y2="160" stroke="#71717a" strokeWidth="0.5" />
      <text x="22" y="163" fontSize="9" fill="#e4e4e7">Low</text>

      {/* Bearish (red) */}
      <Candle color="#ef4444" bodyTop={50} bodyBottom={125} wickTop={30} wickBottom={155} label="BEAR" x={250} />
      <line x1="259" y1="50" x2="285" y2="50" stroke="#71717a" strokeWidth="0.5" />
      <text x="288" y="53" fontSize="9" fill="#e4e4e7">Open</text>
      <line x1="259" y1="125" x2="285" y2="125" stroke="#71717a" strokeWidth="0.5" />
      <text x="288" y="128" fontSize="9" fill="#e4e4e7">Close</text>
    </svg>
  );
}

function PatternCard({ title, direction, meaning, svg }) {
  const dirColor = direction === "bullish" ? "text-emerald-400 bg-emerald-500/15 border-emerald-500/30"
    : direction === "bearish" ? "text-rose-400 bg-rose-500/15 border-rose-500/30"
    : "text-zinc-400 bg-zinc-700/30 border-zinc-600/30";
  return (
    <div className="p-3 sm:p-4 rounded-sm bg-zinc-900/60 border border-zinc-800/80 flex gap-3 items-start">
      <div className="shrink-0 w-16 h-20 bg-zinc-950/60 border border-zinc-800 rounded-sm flex items-center justify-center">
        {svg}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap mb-1">
          <p className="text-xs font-medium text-zinc-100">{title}</p>
          <Badge className={`text-[8px] px-1.5 py-0 h-4 border rounded-sm uppercase ${dirColor}`}>{direction}</Badge>
        </div>
        <p className="text-[11px] text-zinc-400 leading-relaxed">{meaning}</p>
      </div>
    </div>
  );
}

// Small illustrative SVGs for patterns
const HammerSvg = (
  <svg viewBox="0 0 40 60" className="w-8 h-14">
    <line x1="20" y1="8" x2="20" y2="20" stroke="#10b981" strokeWidth="1.5" />
    <rect x="13" y="20" width="14" height="12" fill="#10b981" rx="1" />
    <line x1="20" y1="32" x2="20" y2="54" stroke="#10b981" strokeWidth="1.5" />
  </svg>
);
const ShootingStarSvg = (
  <svg viewBox="0 0 40 60" className="w-8 h-14">
    <line x1="20" y1="6" x2="20" y2="28" stroke="#ef4444" strokeWidth="1.5" />
    <rect x="13" y="28" width="14" height="12" fill="#ef4444" rx="1" />
    <line x1="20" y1="40" x2="20" y2="52" stroke="#ef4444" strokeWidth="1.5" />
  </svg>
);
const DojiSvg = (
  <svg viewBox="0 0 40 60" className="w-8 h-14">
    <line x1="20" y1="8" x2="20" y2="52" stroke="#a1a1aa" strokeWidth="1.5" />
    <rect x="10" y="29" width="20" height="2" fill="#a1a1aa" />
  </svg>
);
const EngulfingBullSvg = (
  <svg viewBox="0 0 60 60" className="w-12 h-14">
    <line x1="15" y1="15" x2="15" y2="45" stroke="#ef4444" strokeWidth="1.5" />
    <rect x="9" y="24" width="12" height="12" fill="#ef4444" rx="1" />
    <line x1="42" y1="8" x2="42" y2="52" stroke="#10b981" strokeWidth="1.5" />
    <rect x="34" y="14" width="16" height="30" fill="#10b981" rx="1" />
  </svg>
);
const EngulfingBearSvg = (
  <svg viewBox="0 0 60 60" className="w-12 h-14">
    <line x1="15" y1="20" x2="15" y2="40" stroke="#10b981" strokeWidth="1.5" />
    <rect x="9" y="25" width="12" height="10" fill="#10b981" rx="1" />
    <line x1="42" y1="8" x2="42" y2="52" stroke="#ef4444" strokeWidth="1.5" />
    <rect x="34" y="14" width="16" height="30" fill="#ef4444" rx="1" />
  </svg>
);

export default function Trading() {
  return (
    <div className="p-5 sm:p-6 space-y-5 max-w-[1100px] overflow-x-hidden" data-testid="trading-page">
      {/* Hero */}
      <div className="rounded-sm border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-zinc-900/30 to-zinc-950 p-5 sm:p-6">
        <div className="flex items-center gap-2.5 mb-2">
          <ChartLine size={22} weight="bold" className="text-amber-400" />
          <Badge className="text-[9px] px-1.5 py-0 h-4 border-0 rounded-sm bg-amber-500/15 text-amber-300">FOUNDATION</Badge>
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-zinc-50 tracking-tight">Trading — The Basics</h1>
        <p className="text-xs sm:text-sm text-zinc-400 mt-2 max-w-2xl">
          A plain-English intro to trading stocks &amp; crypto. We'll cover what trading is, how to read a candlestick chart,
          the handful of patterns that actually matter, and the basic rules that keep beginners from blowing up an account.
        </p>
      </div>

      {/* WHAT IS TRADING */}
      <Section
        icon={Question}
        title="What is trading?"
        subtitle="The difference between trading and investing"
        tone="amber"
        testid="trading-what"
      >
        <p>
          Trading is <span className="text-amber-300 font-medium">buying an asset with the goal of selling it for a higher price</span> — usually
          over a shorter time frame than investing. An investor buys a stock and holds it for years. A trader might hold for minutes, hours, days, or weeks.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
          <div className="p-3 rounded-sm bg-zinc-900/60 border border-zinc-800/80">
            <p className="text-xs font-medium text-zinc-100 mb-1">Day Trading</p>
            <p className="text-[11px] text-zinc-400 leading-relaxed">Buy and sell within the same day. Focused, intense, and taxed harder.</p>
          </div>
          <div className="p-3 rounded-sm bg-zinc-900/60 border border-zinc-800/80">
            <p className="text-xs font-medium text-zinc-100 mb-1">Swing Trading</p>
            <p className="text-[11px] text-zinc-400 leading-relaxed">Hold for a few days to a few weeks. Ride a short-term price "swing."</p>
          </div>
          <div className="p-3 rounded-sm bg-zinc-900/60 border border-zinc-800/80">
            <p className="text-xs font-medium text-zinc-100 mb-1">Position Trading</p>
            <p className="text-[11px] text-zinc-400 leading-relaxed">Hold for weeks to months. Based on bigger trends, not daily noise.</p>
          </div>
        </div>

        <div className="mt-4 p-3 rounded-sm border border-zinc-800 bg-zinc-950/60">
          <p className="text-[11px] text-zinc-500 uppercase tracking-wider mb-1">Honest warning</p>
          <p className="text-xs text-zinc-300">
            Most beginner traders lose money — not because trading is impossible, but because they skip the education and risk management.
            Read this page, paper-trade for a while, and only then risk real money.
          </p>
        </div>
      </Section>

      {/* HOW TO READ A CANDLE */}
      <Section
        icon={Clock}
        title="How to read a candlestick"
        subtitle="The 4 prices every candle tells you — at a glance"
        tone="emerald"
        testid="trading-candle"
      >
        <p>
          Each candle shows price action for a set time period (1 minute, 1 hour, 1 day, etc.). It packs <span className="text-emerald-300 font-medium">four prices</span> into one shape:
          <strong className="text-zinc-100"> Open</strong>, <strong className="text-zinc-100">High</strong>,
          <strong className="text-zinc-100"> Low</strong>, and <strong className="text-zinc-100">Close</strong>.
        </p>

        <div className="p-4 rounded-sm bg-zinc-950/60 border border-zinc-800" data-testid="candle-diagram">
          <BullBearDiagram />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          <div className="p-3 rounded-sm bg-emerald-500/5 border border-emerald-500/20">
            <div className="flex items-center gap-2 mb-1">
              <CaretUp size={14} weight="fill" className="text-emerald-400" />
              <p className="text-xs font-medium text-emerald-300">Green / Bullish candle</p>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">Price <em>closed higher</em> than it opened. Buyers won the time period.</p>
          </div>
          <div className="p-3 rounded-sm bg-rose-500/5 border border-rose-500/20">
            <div className="flex items-center gap-2 mb-1">
              <CaretDown size={14} weight="fill" className="text-rose-400" />
              <p className="text-xs font-medium text-rose-300">Red / Bearish candle</p>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">Price <em>closed lower</em> than it opened. Sellers won the time period.</p>
          </div>
        </div>

        <div className="mt-3 p-3 rounded-sm border border-zinc-800 bg-zinc-950/60">
          <p className="text-[11px] text-zinc-500 uppercase tracking-wider mb-1">Plain-English breakdown</p>
          <ul className="space-y-1.5 text-xs text-zinc-300">
            <li><strong className="text-zinc-100">Body</strong> — the fat part, from Open to Close. Big body = strong move. Small body = indecision.</li>
            <li><strong className="text-zinc-100">Wick / Shadow</strong> — the thin lines above &amp; below. They show the <em>highest</em> and <em>lowest</em> price reached during the period, even if price came back.</li>
            <li><strong className="text-zinc-100">Long upper wick</strong> — buyers pushed high, but sellers pushed it back down. Weakness.</li>
            <li><strong className="text-zinc-100">Long lower wick</strong> — sellers pushed low, but buyers brought it back up. Strength.</li>
          </ul>
        </div>
      </Section>

      {/* COMMON PATTERNS */}
      <Section
        icon={Target}
        title="Candle patterns that actually matter"
        subtitle="A handful of simple shapes that show up again and again"
        tone="blue"
        testid="trading-patterns"
      >
        <p>You don't need to memorize 50 patterns. Start with these five — they're the most reliable and the easiest to spot.</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <PatternCard
            title="Hammer"
            direction="bullish"
            meaning="Long lower wick, small body up top. Buyers stepped in after a sell-off. Often shows up at the bottom of a downtrend."
            svg={HammerSvg}
          />
          <PatternCard
            title="Shooting Star"
            direction="bearish"
            meaning="Long upper wick, small body at the bottom. Buyers tried to push up but sellers knocked it back. Often shows up near a top."
            svg={ShootingStarSvg}
          />
          <PatternCard
            title="Doji"
            direction="neutral"
            meaning="Open ≈ Close, tiny/no body. Pure indecision. Important when it shows up after a big move — it often signals a pause or reversal."
            svg={DojiSvg}
          />
          <PatternCard
            title="Bullish Engulfing"
            direction="bullish"
            meaning="A small red candle followed by a bigger green candle that completely 'swallows' it. Shows buyers taking over."
            svg={EngulfingBullSvg}
          />
          <PatternCard
            title="Bearish Engulfing"
            direction="bearish"
            meaning="Small green candle followed by a bigger red candle that engulfs it. Sellers taking control."
            svg={EngulfingBearSvg}
          />
          <div className="p-3 sm:p-4 rounded-sm bg-zinc-900/60 border border-zinc-800/80 flex items-center gap-3">
            <Sparkle size={14} weight="fill" className="text-amber-400 shrink-0" />
            <p className="text-[11px] text-zinc-400 leading-relaxed">
              <strong className="text-zinc-200">Context is everything.</strong> The same pattern means different things at the top of a rally vs. the bottom of a crash.
              Never trade a pattern alone — combine it with trend and support/resistance.
            </p>
          </div>
        </div>
      </Section>

      {/* CHART CONCEPTS */}
      <Section
        icon={Scales}
        title="Reading a chart beyond candles"
        subtitle="Trend, support, resistance, and volume"
        tone="amber"
        testid="trading-chart-concepts"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="p-4 rounded-sm bg-zinc-900/60 border border-zinc-800/80">
            <div className="flex items-center gap-2 mb-1.5">
              <TrendUp size={14} weight="bold" className="text-emerald-400" />
              <p className="text-sm font-medium text-zinc-100">Trend</p>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">
              The <em>overall direction</em>. Higher-highs &amp; higher-lows = uptrend. Lower-highs &amp; lower-lows = downtrend.
              Sideways (flat) = ranging. Trade <span className="text-amber-300">with</span> the trend when starting out.
            </p>
          </div>

          <div className="p-4 rounded-sm bg-zinc-900/60 border border-zinc-800/80">
            <div className="flex items-center gap-2 mb-1.5">
              <Equals size={14} weight="bold" className="text-blue-400" />
              <p className="text-sm font-medium text-zinc-100">Support &amp; Resistance</p>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">
              <strong className="text-emerald-300">Support</strong> = a price level buyers keep defending. <strong className="text-rose-300">Resistance</strong> = a level sellers keep defending.
              Price often bounces off these lines — until it breaks, which can signal a bigger move.
            </p>
          </div>

          <div className="p-4 rounded-sm bg-zinc-900/60 border border-zinc-800/80">
            <div className="flex items-center gap-2 mb-1.5">
              <Gauge size={14} weight="bold" className="text-amber-400" />
              <p className="text-sm font-medium text-zinc-100">Volume</p>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">
              The bars at the bottom of most charts = how much was traded. A move on <em>high volume</em> is taken seriously.
              A move on <em>low volume</em> often reverses. Always glance at volume before reacting to a breakout.
            </p>
          </div>

          <div className="p-4 rounded-sm bg-zinc-900/60 border border-zinc-800/80">
            <div className="flex items-center gap-2 mb-1.5">
              <Clock size={14} weight="bold" className="text-zinc-400" />
              <p className="text-sm font-medium text-zinc-100">Timeframes</p>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">
              The same chart looks different at 1m vs. 1D. Zoom out first to see the big trend, then zoom in to pick your entry.
              Conflicting signals across timeframes = sit out.
            </p>
          </div>
        </div>
      </Section>

      {/* ORDER TYPES */}
      <Section
        icon={Target}
        title="The 4 order types you actually need"
        subtitle="Forget exotic orders — master these first"
        tone="emerald"
        testid="trading-orders"
      >
        <ol className="space-y-2.5 pl-1">
          <li className="flex gap-3">
            <span className="w-6 h-6 shrink-0 rounded-sm bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-300 text-[10px] font-bold">1</span>
            <div>
              <p className="text-sm font-medium text-zinc-100">Market Order</p>
              <p className="text-[11px] text-zinc-400 leading-relaxed">"Buy / sell right now, at whatever the price is." Fastest. Can slip in thin markets. Fine for big, liquid assets.</p>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 shrink-0 rounded-sm bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-300 text-[10px] font-bold">2</span>
            <div>
              <p className="text-sm font-medium text-zinc-100">Limit Order</p>
              <p className="text-[11px] text-zinc-400 leading-relaxed">"Only buy at $X or lower / sell at $X or higher." You control the price. It only fills if the market reaches you.</p>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 shrink-0 rounded-sm bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-300 text-[10px] font-bold">3</span>
            <div>
              <p className="text-sm font-medium text-zinc-100">Stop-Loss</p>
              <p className="text-[11px] text-zinc-400 leading-relaxed">"If price drops to $X, sell automatically." This is how you protect the downside. <span className="text-amber-300">Never trade without one.</span></p>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 shrink-0 rounded-sm bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-300 text-[10px] font-bold">4</span>
            <div>
              <p className="text-sm font-medium text-zinc-100">Take-Profit</p>
              <p className="text-[11px] text-zinc-400 leading-relaxed">"If price hits $Y, sell automatically." This is how you lock in wins without having to stare at the chart.</p>
            </div>
          </li>
        </ol>
      </Section>

      {/* RISK MANAGEMENT */}
      <Section
        icon={ShieldCheck}
        title="The only rules that keep you alive"
        subtitle="Risk management — more important than any chart pattern"
        tone="blue"
        testid="trading-risk"
      >
        <p>
          Winning traders and losing traders often see the same setups. The difference is <span className="text-blue-300 font-medium">how much they risk per trade</span> and
          <span className="text-blue-300 font-medium"> how they cut losses</span>. Master this and you're already ahead of 90% of beginners.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="p-4 rounded-sm bg-blue-500/5 border border-blue-500/20">
            <p className="text-sm font-medium text-blue-200 mb-1">The 1–2% Rule</p>
            <p className="text-[11px] text-zinc-400 leading-relaxed">Never risk more than 1–2% of your total account on a single trade. If you have $5,000, your max loss on any one trade is $50–$100.</p>
          </div>
          <div className="p-4 rounded-sm bg-blue-500/5 border border-blue-500/20">
            <p className="text-sm font-medium text-blue-200 mb-1">Risk / Reward ≥ 1 : 2</p>
            <p className="text-[11px] text-zinc-400 leading-relaxed">Aim to make at least twice what you risk. If your stop is $50 away, your target should be $100+ away. You can be wrong half the time and still win.</p>
          </div>
          <div className="p-4 rounded-sm bg-blue-500/5 border border-blue-500/20">
            <p className="text-sm font-medium text-blue-200 mb-1">Cut losses fast</p>
            <p className="text-[11px] text-zinc-400 leading-relaxed">When the stop hits, exit — no arguing with the chart. "Hoping" is not a strategy. Small losses are survivable; big ones aren't.</p>
          </div>
          <div className="p-4 rounded-sm bg-blue-500/5 border border-blue-500/20">
            <p className="text-sm font-medium text-blue-200 mb-1">Let winners run</p>
            <p className="text-[11px] text-zinc-400 leading-relaxed">Move your stop up as price moves in your favor (trailing stop). Most losing traders cap their upside and let their downside stay open.</p>
          </div>
        </div>
      </Section>

      {/* COMMON MISTAKES */}
      <Section
        icon={Warning}
        title="Beginner mistakes to avoid"
        subtitle="Almost everyone makes these — you don't have to"
        tone="rose"
        testid="trading-mistakes"
      >
        <ul className="space-y-2">
          <li className="flex items-start gap-2.5">
            <TrendDown size={13} weight="bold" className="text-rose-400 mt-1 shrink-0" />
            <p className="text-xs sm:text-sm text-zinc-300"><strong className="text-zinc-100">Trading without a stop-loss.</strong> One bad trade can take out months of gains.</p>
          </li>
          <li className="flex items-start gap-2.5">
            <TrendDown size={13} weight="bold" className="text-rose-400 mt-1 shrink-0" />
            <p className="text-xs sm:text-sm text-zinc-300"><strong className="text-zinc-100">Revenge trading.</strong> Losing money and immediately jumping back in "to win it back." Almost always ends worse.</p>
          </li>
          <li className="flex items-start gap-2.5">
            <TrendDown size={13} weight="bold" className="text-rose-400 mt-1 shrink-0" />
            <p className="text-xs sm:text-sm text-zinc-300"><strong className="text-zinc-100">Oversizing on leverage.</strong> 10x leverage on crypto feels exciting — until a 10% move wipes you out.</p>
          </li>
          <li className="flex items-start gap-2.5">
            <TrendDown size={13} weight="bold" className="text-rose-400 mt-1 shrink-0" />
            <p className="text-xs sm:text-sm text-zinc-300"><strong className="text-zinc-100">FOMO buying green candles.</strong> By the time it's pumping on Twitter, the smart money is already selling to you.</p>
          </li>
          <li className="flex items-start gap-2.5">
            <TrendDown size={13} weight="bold" className="text-rose-400 mt-1 shrink-0" />
            <p className="text-xs sm:text-sm text-zinc-300"><strong className="text-zinc-100">No trading plan.</strong> Every trade should have an entry, a stop, a target, and a reason — written before you click buy.</p>
          </li>
          <li className="flex items-start gap-2.5">
            <TrendDown size={13} weight="bold" className="text-rose-400 mt-1 shrink-0" />
            <p className="text-xs sm:text-sm text-zinc-300"><strong className="text-zinc-100">Ignoring fees &amp; taxes.</strong> Frequent trading means frequent fees and every realized gain is taxable. Factor it in.</p>
          </li>
        </ul>
      </Section>

      {/* Next steps */}
      <div className="rounded-sm border border-amber-500/25 bg-zinc-900/40 p-5 sm:p-6" data-testid="trading-next-steps">
        <div className="flex items-center gap-2 mb-3">
          <ArrowRight size={16} weight="bold" className="text-amber-400" />
          <h3 className="font-heading text-base font-bold text-zinc-100">Practice before you play</h3>
        </div>
        <p className="text-xs sm:text-sm text-zinc-400 leading-relaxed mb-3">
          The best way to learn is to <strong className="text-zinc-200">paper trade</strong> — simulate trades with fake money — for at least a few weeks.
          Watch real candles form in real time, make real decisions, and see real outcomes. Then risk a tiny amount of real money and scale up slowly.
        </p>
        <p className="text-[11px] text-zinc-500 mb-4">
          When you're ready, The Golden Nest's <span className="text-amber-300">Forecasting</span> and <span className="text-amber-300">Asset Detail</span> pages show you real-time indicators and AI analysis
          you can practice against.
        </p>
      </div>
    </div>
  );
}
